import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Link } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import WomensIcon from '@mui/icons-material/Woman';
import PeopleIcon from '@mui/icons-material/People';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import api from '../services/api';

// Try to import logo, fallback to null if not found
let logo;
try {
  logo = require('../assets/logo.png');
} catch (e) {
  logo = null;
}

const Home = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const heroImages = ['/hero-1.jpg', '/hero-2.jpg', '/hero-3.jpg'];
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [reachStats, setReachStats] = useState({
    activeMembers: 500,
    events: 50,
    studentsMentored: null,
    fundsRaised: null,
  });
  const [storyPosts, setStoryPosts] = useState([]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [heroImages.length]);

  const currentHeroImage = heroImages[currentImageIndex];

  // Load dynamic stats and stories
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, eventsRes, postsRes] = await Promise.all([
          api.get('/community/stats'),
          api.get('/events'),
          api.get('/posts', { params: { limit: 3 } }),
        ]);

        const totalMembers =
          statsRes.data?.data?.totalMembers ?? reachStats.activeMembers;
        const totalEvents =
          eventsRes.data?.total ?? eventsRes.data?.count ?? reachStats.events;

        setReachStats((prev) => ({
          ...prev,
          activeMembers: totalMembers,
          events: totalEvents || prev.events,
        }));

        const posts = postsRes.data?.data || [];
        if (posts.length) {
          setStoryPosts(
            posts.slice(0, 3).map((post) => ({
              id: post._id,
              title: post.title,
              category: post.category,
              excerpt:
                post.excerpt ||
                (post.content
                  ? `${post.content.slice(0, 160)}…`
                  : 'Read more about this story.'),
            }))
          );
        }
      } catch (error) {
        console.error('Error loading homepage data:', error);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          color: '#111827',
          pt: { xs: 6, md: 8 },
          pb: { xs: 8, md: 10 },
        }}
      >
        {/* Moving background images */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${currentHeroImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: 'scale(1.05)',
            transition: 'opacity 1.2s ease, transform 1.2s ease',
            opacity: 0.9,
          }}
        />
        {/* Softer gradient overlay for readability while keeping images vivid */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(90deg, rgba(248,250,252,0.96) 0%, rgba(248,250,252,0.9) 35%, rgba(248,250,252,0.35) 70%, rgba(248,250,252,0.1) 100%)',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  textAlign: { xs: 'center', md: 'left' },
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                {logo ? (
                  <Box
                    component="img"
                    src={logo}
                    alt="GBCN Foundation Logo"
                    sx={{
                      height: { xs: '90px', md: '140px' },
                      width: 'auto',
                      objectFit: 'contain',
                      mb: { xs: 2, md: 3 },
                    }}
                  />
                ) : (
                  <Typography
                    variant={isMobile ? 'h3' : 'h2'}
                    component="p"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      color: '#f97316',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                    }}
                  >
                    GBCN Foundation
                  </Typography>
                )}
                <Typography
                  variant={isMobile ? 'h4' : 'h3'}
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    mb: { xs: 1.5, md: 2 },
                    color: '#111827',
                    lineHeight: 1.2,
                    fontSize: { xs: '1.75rem', sm: '2rem', md: '2.4rem' },
                  }}
                >
                  Empowering communities through education, culture, and mentorship.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#4b5563',
                    mb: { xs: 3, md: 4 },
                    maxWidth: '600px',
                    fontSize: { xs: '0.9rem', md: '1.05rem' },
                    lineHeight: 1.7,
                    mx: { xs: 'auto', md: 0 },
                  }}
                >
                  We are building a transparent, community-led movement to nurture the next
                  generation of leaders rooted in Indian values and Sanskriti.
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                  }}
                >
                  <Button
                    component={Link}
                    to="/donate"
                    variant="contained"
                    size="large"
                    sx={{
                      backgroundColor: '#f97316',
                      color: '#ffffff',
                      px: 4,
                      py: 1.5,
                      fontSize: '1.05rem',
                      fontWeight: 600,
                      boxShadow: '0 18px 45px rgba(249, 115, 22, 0.45)',
                      '&:hover': {
                        backgroundColor: '#ea580c',
                        boxShadow: '0 22px 55px rgba(234, 88, 12, 0.55)',
                      },
                    }}
                  >
                    Donate Now
                  </Button>
                  <Button
                    component={Link}
                    to="/register"
                    variant="outlined"
                    size="large"
                    sx={{
                      borderColor: '#111827',
                      color: '#111827',
                      px: 4,
                      py: 1.5,
                      fontSize: '1.05rem',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: '#111827',
                        backgroundColor: 'rgba(15,23,42,0.06)',
                      },
                    }}
                  >
                    Join the Movement
                  </Button>
                  <Button
                    component={Link}
                    to="/members/refer"
                    variant="text"
                    size="large"
                    sx={{
                      color: '#f97316',
                      fontWeight: 600,
                    }}
                  >
                    Refer a Member
                  </Button>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  height: { xs: 'auto', md: 360 },
                  mt: { xs: 4, md: 0 },
                }}
              >
                {/* Hero image placeholder – replace backgroundImage with your own photos */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 6,
                    overflow: 'hidden',
                    backgroundImage:
                      "url('/hero-1.jpg')",
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    boxShadow: '0 24px 60px rgba(15,23,42,0.35)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      background:
                        'linear-gradient(135deg, rgba(15,23,42,0.55), rgba(15,23,42,0.25))',
                    },
                  }}
                />
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    borderRadius: 4,
                    backgroundColor: 'rgba(255,255,255,0.96)',
                    border: '1px solid rgba(148,163,184,0.4)',
                    boxShadow: '0 18px 45px rgba(15,23,42,0.25)',
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    height: { xs: 'auto', md: '100%' },
                  }}
                >
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ textTransform: 'uppercase', color: '#6b7280', mb: 1 }}
                    >
                      At a glance
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: '#111827', mb: 1 }}
                    >
                      A connected global community.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#4b5563', mb: 2 }}>
                      From education to livelihood, we are building a trusted platform where
                      members support each other and give back to society.
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box>
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: 700, color: '#f97316', mb: 0.5 }}
                        >
                          {reachStats.activeMembers}+
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#4b5563' }}>
                          Active members
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box>
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: 700, color: '#2563eb', mb: 0.5 }}
                        >
                          {reachStats.events}+
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#4b5563' }}>
                          Impactful events
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: '#16a34a', mb: 0.5 }}
                >
                  {reachStats.studentsMentored != null
                    ? `${reachStats.studentsMentored}+`
                    : '—'}
                </Typography>
                        <Typography variant="body2" sx={{ color: '#4b5563' }}>
                          Students mentored
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}
                >
                  {reachStats.fundsRaised || '—'}
                </Typography>
                        <Typography variant="body2" sx={{ color: '#4b5563' }}>
                          Funds raised
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Impact Highlights */}
      <Box
        sx={{
          py: 8,
          background: 'linear-gradient(180deg, #f9fafb 0%, #eef2ff 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              mb: 2,
              color: '#111827',
            }}
          >
            Our Reach
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              color: '#4b5563',
              maxWidth: '640px',
              mx: 'auto',
              mb: 5,
            }}
          >
            Like leading foundations, we measure our work in lives touched and opportunities
            created – every member, event, and mentorship contributes to a larger movement.
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={6} md={3}>
              <Box
                sx={{
                  textAlign: 'center',
                  backgroundColor: '#ffffff',
                  borderRadius: 3,
                  p: 3,
                  boxShadow: '0 10px 25px rgba(15,23,42,0.06)',
                  border: '1px solid rgba(148,163,184,0.35)',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    color: '#f97316',
                    mb: 0.5,
                  }}
                >
                  {reachStats.activeMembers}+
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{ color: '#111827', fontWeight: 600 }}
                >
                  Active Members
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box
                sx={{
                  textAlign: 'center',
                  backgroundColor: '#ffffff',
                  borderRadius: 3,
                  p: 3,
                  boxShadow: '0 10px 25px rgba(15,23,42,0.06)',
                  border: '1px solid rgba(148,163,184,0.35)',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    color: '#2563eb',
                    mb: 0.5,
                  }}
                >
                  {reachStats.events}+
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{ color: '#111827', fontWeight: 600 }}
                >
                  Events Conducted
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box
                sx={{
                  textAlign: 'center',
                  backgroundColor: '#ffffff',
                  borderRadius: 3,
                  p: 3,
                  boxShadow: '0 10px 25px rgba(15,23,42,0.06)',
                  border: '1px solid rgba(148,163,184,0.35)',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    color: '#16a34a',
                    mb: 0.5,
                  }}
                >
                  {reachStats.studentsMentored != null
                    ? `${reachStats.studentsMentored}+`
                    : '—'}
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{ color: '#111827', fontWeight: 600 }}
                >
                  Students Mentored
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box
                sx={{
                  textAlign: 'center',
                  backgroundColor: '#ffffff',
                  borderRadius: 3,
                  p: 3,
                  boxShadow: '0 10px 25px rgba(15,23,42,0.06)',
                  border: '1px solid rgba(148,163,184,0.35)',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    color: '#0f172a',
                    mb: 0.5,
                  }}
                >
                  {reachStats.fundsRaised || '—'}
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{ color: '#111827', fontWeight: 600 }}
                >
                  Funds Raised
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stories of Change style section */}
      <Box
        sx={{
          py: 8,
          backgroundColor: '#0f172a',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              mb: 2,
              color: '#f9fafb',
            }}
          >
            Stories of Change
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              color: '#e5e7eb',
              maxWidth: '700px',
              mx: 'auto',
              mb: 5,
            }}
          >
            Real journeys from our community that show how mentorship, education, and collective
            support can change lives.
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: '1px solid rgba(148,163,184,0.5)',
                  boxShadow: '0 20px 45px rgba(15,23,42,0.6)',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    height: 180,
                    backgroundImage: "url('/hero-2.jpg')",
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background:
                        'linear-gradient(180deg, rgba(15,23,42,0.1) 0%, rgba(15,23,42,0.9) 100%)',
                    }}
                  />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      position: 'absolute',
                      bottom: 12,
                      left: 16,
                      color: '#f9fafb',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      fontWeight: 600,
                    }}
                  >
                    {storyPosts[0]?.category || 'Student Mentorship'}
                  </Typography>
                </Box>
                <CardContent sx={{ p: 3, backgroundColor: '#020617' }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 1, color: '#f9fafb' }}
                  >
                    {storyPosts[0]?.title || 'From doubt to confidence'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#e5e7eb', mb: 2 }}>
                    {storyPosts[0]?.excerpt ||
                      'A first-generation learner found clarity and support through our mentor network.'}
                  </Typography>
                  <Button
                    component={Link}
                    to="/activities"
                    variant="text"
                    sx={{ color: '#fb923c', fontWeight: 600 }}
                  >
                    Explore programs →
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: '1px solid rgba(148,163,184,0.5)',
                  boxShadow: '0 20px 45px rgba(15,23,42,0.6)',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    height: 180,
                    backgroundImage: "url('/hero-3.jpg')",
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background:
                        'linear-gradient(180deg, rgba(15,23,42,0.1) 0%, rgba(15,23,42,0.9) 100%)',
                    }}
                  />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      position: 'absolute',
                      bottom: 12,
                      left: 16,
                      color: '#f9fafb',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      fontWeight: 600,
                    }}
                  >
                    {storyPosts[1]?.category || 'Community Support'}
                  </Typography>
                </Box>
                <CardContent sx={{ p: 3, backgroundColor: '#020617' }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 1, color: '#f9fafb' }}
                  >
                    {storyPosts[1]?.title || 'Standing together in crisis'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#e5e7eb', mb: 2 }}>
                    {storyPosts[1]?.excerpt ||
                      'Members came together to support a family with education and emergency assistance.'}
                  </Typography>
                  <Button
                    component={Link}
                    to="/donate"
                    variant="text"
                    sx={{ color: '#fb923c', fontWeight: 600 }}
                  >
                    Support a campaign →
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: '1px solid rgba(148,163,184,0.5)',
                  boxShadow: '0 20px 45px rgba(15,23,42,0.6)',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    height: 180,
                    backgroundImage: "url('/hero-4.jpg')",
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background:
                        'linear-gradient(180deg, rgba(15,23,42,0.1) 0%, rgba(15,23,42,0.9) 100%)',
                    }}
                  />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      position: 'absolute',
                      bottom: 12,
                      left: 16,
                      color: '#f9fafb',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      fontWeight: 600,
                    }}
                  >
                    {storyPosts[2]?.category || 'Youth Leadership'}
                  </Typography>
                </Box>
                <CardContent sx={{ p: 3, backgroundColor: '#020617' }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 1, color: '#f9fafb' }}
                  >
                    {storyPosts[2]?.title || 'Giving back to the network'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#e5e7eb', mb: 2 }}>
                    {storyPosts[2]?.excerpt ||
                      'A young professional who once received guidance now leads sessions for new students.'}
                  </Typography>
                  <Button
                    component={Link}
                    to="/events"
                    variant="text"
                    sx={{ color: '#fb923c', fontWeight: 600 }}
                  >
                    See upcoming events →
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Key Focus Areas / Programs */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          component="h2"
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            mb: 2,
            color: '#111827',
          }}
        >
          Our Programs
        </Typography>
        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            color: 'text.secondary',
            mb: 6,
            maxWidth: '700px',
            mx: 'auto',
          }}
        >
          GBCN Foundation is committed to transparent governance, community participation, and digital accountability.
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                background:
                  'radial-gradient(circle at top left, #fff7ed 0%, #ffffff 55%, #e5f2ff 100%)',
                borderRadius: 4,
                border: '1px solid #fee2e2',
                boxShadow: '0 18px 45px rgba(15,23,42,0.06)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 22px 55px rgba(249,115,22,0.22)',
                  borderColor: '#fdba74',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <SchoolIcon
                  sx={{
                    fontSize: 48,
                    color: '#f97316',
                    mb: 2,
                  }}
                />
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 600, color: '#111827' }}
                >
                  Education Upliftment
                </Typography>
                <Typography variant="body2" sx={{ color: '#4b5563', mb: 1.5 }}>
                  Supporting educational initiatives, scholarships, and student mentorship programs
                </Typography>
                <Button
                  component={Link}
                  to="/activities"
                  size="small"
                  variant="text"
                  sx={{ color: '#f97316', fontWeight: 600, px: 0 }}
                >
                  View education programs →
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                background:
                  'radial-gradient(circle at top left, #fdf2ff 0%, #ffffff 55%, #e0f2fe 100%)',
                borderRadius: 4,
                border: '1px solid #e5e7eb',
                boxShadow: '0 18px 45px rgba(15,23,42,0.06)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 22px 55px rgba(236,72,153,0.2)',
                  borderColor: '#f9a8d4',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <WomensIcon
                  sx={{
                    fontSize: 48,
                    color: '#f97316',
                    mb: 2,
                  }}
                />
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 600, color: '#111827' }}
                >
                  Women Empowerment
                </Typography>
                <Typography variant="body2" sx={{ color: '#4b5563', mb: 1.5 }}>
                  Empowering women through skill development, leadership programs, and support initiatives
                </Typography>
                <Button
                  component={Link}
                  to="/activities"
                  size="small"
                  variant="text"
                  sx={{ color: '#f97316', fontWeight: 600, px: 0 }}
                >
                  Explore women initiatives →
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                background:
                  'radial-gradient(circle at top left, #eff6ff 0%, #ffffff 55%, #fef9c3 100%)',
                borderRadius: 4,
                border: '1px solid #e5e7eb',
                boxShadow: '0 18px 45px rgba(15,23,42,0.06)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 22px 55px rgba(37,99,235,0.17)',
                  borderColor: '#bfdbfe',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <PeopleIcon
                  sx={{
                    fontSize: 48,
                    color: '#f97316',
                    mb: 2,
                  }}
                />
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 600, color: '#111827' }}
                >
                  Youth Empowerment
                </Typography>
                <Typography variant="body2" sx={{ color: '#4b5563', mb: 1.5 }}>
                  Mentoring and empowering youth through career guidance and leadership development
                </Typography>
                <Button
                  component={Link}
                  to="/activities"
                  size="small"
                  variant="text"
                  sx={{ color: '#f97316', fontWeight: 600, px: 0 }}
                >
                  View youth programs →
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                background:
                  'radial-gradient(circle at top left, #fef9c3 0%, #ffffff 55%, #fee2e2 100%)',
                borderRadius: 4,
                border: '1px solid #e5e7eb',
                boxShadow: '0 18px 45px rgba(15,23,42,0.06)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 22px 55px rgba(202,138,4,0.2)',
                  borderColor: '#facc15',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <MenuBookIcon
                  sx={{
                    fontSize: 48,
                    color: '#f97316',
                    mb: 2,
                  }}
                />
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 600, color: '#111827' }}
                >
                  Culture & Sanskriti
                </Typography>
                <Typography variant="body2" sx={{ color: '#4b5563', mb: 1.5 }}>
                  Preserving and promoting Indian Sanskriti, traditions, and cultural heritage
                </Typography>
                <Button
                  component={Link}
                  to="/activities"
                  size="small"
                  variant="text"
                  sx={{ color: '#f97316', fontWeight: 600, px: 0 }}
                >
                  View culture initiatives →
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                background:
                  'radial-gradient(circle at top left, #ecfeff 0%, #ffffff 55%, #fef2f2 100%)',
                borderRadius: 4,
                border: '1px solid #e5e7eb',
                boxShadow: '0 18px 45px rgba(15,23,42,0.06)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 22px 55px rgba(14,116,144,0.2)',
                  borderColor: '#67e8f9',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <VolunteerActivismIcon
                  sx={{
                    fontSize: 48,
                    color: '#f97316',
                    mb: 2,
                  }}
                />
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 600, color: '#111827' }}
                >
                  Student Mentorship
                </Typography>
                <Typography variant="body2" sx={{ color: '#4b5563', mb: 1.5 }}>
                  Providing guidance, support, and mentorship to students at all levels
                </Typography>
                <Button
                  component={Link}
                  to="/activities"
                  size="small"
                  variant="text"
                  sx={{ color: '#f97316', fontWeight: 600, px: 0 }}
                >
                  Explore mentorship →
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                background:
                  'radial-gradient(circle at top left, #eef2ff 0%, #ffffff 55%, #dcfce7 100%)',
                borderRadius: 4,
                border: '1px solid #e5e7eb',
                boxShadow: '0 18px 45px rgba(15,23,42,0.06)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 22px 55px rgba(16,185,129,0.22)',
                  borderColor: '#bbf7d0',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <AccountBalanceIcon
                  sx={{
                    fontSize: 48,
                    color: '#f97316',
                    mb: 2,
                  }}
                />
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 600, color: '#111827' }}
                >
                  Transparent Governance
                </Typography>
                <Typography variant="body2" sx={{ color: '#4b5563', mb: 1.5 }}>
                  Ensuring transparency, accountability, and community participation in all activities
                </Typography>
                <Button
                  component={Link}
                  to="/transparency"
                  size="small"
                  variant="text"
                  sx={{ color: '#f97316', fontWeight: 600, px: 0 }}
                >
                  View governance details →
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* How We Work / Journey */}
      <Box
        sx={{
          py: 8,
          background: 'linear-gradient(135deg, #020617 0%, #111827 40%, #020617 100%)',
          color: '#e5e7eb',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="overline"
                sx={{
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: '#818cf8',
                }}
              >
                Our approach
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  mt: 1,
                  color: '#f9fafb',
                }}
              >
                How we work with the community
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 3,
                  color: '#e5e7eb',
                  opacity: 0.9,
                }}
              >
                GBCN Foundation brings together families, youth, and professionals across the
                globe. We design initiatives with the community, not just for the community –
                from cultural events and mentorship circles to emergency support campaigns.
              </Typography>
                <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: '#a5b4fc', mb: 0.5, fontWeight: 600 }}>
                      Step 1
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Listen & identify needs
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#cbd5f5' }}>
                      Inputs from members, volunteers, and local groups help us prioritise what
                      matters most.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: '#a5b4fc', mb: 0.5, fontWeight: 600 }}>
                      Step 2
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Co-create programs
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#cbd5f5' }}>
                      Committees and mentors design activities, events, and scholarships.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: '#a5b4fc', mb: 0.5, fontWeight: 600 }}>
                      Step 3
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Implement & support
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#cbd5f5' }}>
                      Volunteers, donors, and partners come together to execute on-ground.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: '#a5b4fc', mb: 0.5, fontWeight: 600 }}>
                      Step 4
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Share & stay transparent
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#cbd5f5' }}>
                      Updates, reports, and financials are shared openly with members.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  borderRadius: 4,
                  border: '1px solid rgba(148,163,184,0.6)',
                  p: 3,
                  background:
                    'radial-gradient(circle at top left, rgba(79,70,229,0.25), transparent 60%)',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ textTransform: 'uppercase', color: '#a5b4fc', mb: 1 }}
                >
                  Featured initiatives
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Mentorship for first-generation graduates
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#e5e7eb', opacity: 0.9 }}>
                    Senior professionals from the community guide students on careers, exams,
                    and values-driven leadership.
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Sanskriti & heritage events
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#e5e7eb', opacity: 0.9 }}>
                    Celebrations, talks, and workshops that keep our cultural roots alive for
                    the next generation.
                  </Typography>
                </Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Community support fund
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#e5e7eb', opacity: 0.9 }}>
                    A transparent fund through which members support education, health, and
                    emergencies.
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  <Button
                    component={Link}
                    to="/activities"
                    variant="contained"
                    size="small"
                    sx={{
                      backgroundColor: '#f97316',
                      color: '#ffffff',
                      '&:hover': { backgroundColor: '#ea580c' },
                    }}
                  >
                    Explore Activities
                  </Button>
                  <Button
                    component={Link}
                    to="/events"
                    variant="outlined"
                    size="small"
                    sx={{
                      borderColor: '#e5e7eb',
                      color: '#e5e7eb',
                      '&:hover': {
                        borderColor: '#ffffff',
                        backgroundColor: 'rgba(148,163,184,0.12)',
                      },
                    }}
                  >
                    View Events
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

            {/* Transparency teaser */}
            <Box sx={{ py: 6, backgroundColor: '#f3f4f6' }}>
        <Container maxWidth="lg">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, mb: 1, color: '#111827' }}
              >
                Transparent by design
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: '#4b5563', maxWidth: 620 }}
              >
                We believe trust is built through openness. Members can review how funds are
                used, what activities are planned, and the impact we are collectively creating.
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}
            >
              <Button
                component={Link}
                to="/transparency"
                variant="outlined"
                sx={{
                  borderColor: '#111827',
                  color: '#111827',
                  '&:hover': {
                    borderColor: '#111827',
                    backgroundColor: 'rgba(15,23,42,0.05)',
                  },
                }}
              >
                View Transparency Dashboard
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Get Involved CTA band */}
      <Box
        sx={{
          py: 6,
          background: 'linear-gradient(135deg, #f97316 0%, #ea580c 40%, #fb923c 100%)',
          color: '#ffffff',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: { xs: 1.5, md: 0.5 },
                }}
              >
                Be part of the GBCN story.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  maxWidth: 520,
                  opacity: 0.95,
                }}
              >
                Become a member, mentor a student, or support a campaign – your participation
                helps shape a stronger, more connected community.
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1.5 }}
            >
              <Button
                component={Link}
                to="/register"
                variant="contained"
                sx={{
                  backgroundColor: '#0f172a',
                  boxShadow: '0 18px 45px rgba(15,23,42,0.55)',
                  borderRadius: 999,
                  px: 3.5,
                  py: 1.2,
                  fontWeight: 600,
                  color: '#ffffff',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: '#020617' },
                }}
              >
                Join as Member
              </Button>
              <Button
                component={Link}
                to="/donate"
                variant="outlined"
                sx={{
                  borderColor: '#fffbeb',
                  color: '#fffbeb',
                  '&:hover': {
                    borderColor: '#ffffff',
                    backgroundColor: 'rgba(248,250,252,0.1)',
                  },
                }}
              >
                Donate
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>


    </Box>
  );
};

export default Home;
